// src/contexts/PaymentContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from "react";
import type { Plan, PaymentStatusResponse } from "../types/payments";
import api from "../services/apiService";

interface PaymentContextType {
  selectedPlan: Plan;
  setSelectedPlan: (plan: Plan) => void;
  initiateCheckout: (plan: Plan) => void;
  fetchPaymentStatus: (id: string) => Promise<PaymentStatusResponse>;
  paymentStatus: PaymentStatusResponse | null;
  loading: boolean;
  error: string | null;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<Plan>("basic");
  const [paymentStatus, setPaymentStatus] =
    useState<PaymentStatusResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 1) Create Checkout Session & redirect
  const initiateCheckout = async (plan: Plan) => {
    setLoading(true);
    setError(null);
    try {
      // backend returns { sessionId, url }
      const { data } = await api.post<{ sessionId: string; url: string | null }>(
        "/payments/create-checkout-session",
        { plan }
      );

      // Use the checkout URL directly instead of redirectToCheckout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned from server.");
      }
    } catch (err: any) {
      setError(err.message || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  // 2) Existing status fetch (you can keep your usePayments hook here if you like)
  const fetchPaymentStatus = async (
    sessionId: string
  ): Promise<PaymentStatusResponse> => {
    // we assume your backend maps sessionId → status for you
    const resp = await api.get<PaymentStatusResponse>("/payments/status", {
      params: { sessionId },
    });
    setPaymentStatus(resp.data);
    return resp.data;
  };

  const value = useMemo(
    () => ({
      selectedPlan,
      setSelectedPlan,
      initiateCheckout,
      fetchPaymentStatus,
      paymentStatus,
      loading,
      error,
    }),
    [selectedPlan, paymentStatus, loading, error]
  );

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};

export const usePaymentContext = (): PaymentContextType => {
  const ctx = useContext(PaymentContext);
  if (!ctx) throw new Error("usePaymentContext must be inside PaymentProvider");
  return ctx;
};
